import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useQuery } from '@tanstack/react-query';

import {
  addLegacyReconciliationEvidence,
  fetchLegacyReconciliations,
  legacyReconciliationAttachmentsApi,
  verifyLegacyReconciliation,
} from '#/api/crmMgmt/customerOrder/collection';
import { ATTACHMENT_POLICY_STANDARD } from '#/api/platform/attachments';
import { PERM } from '#/config/permissions';
import { DocumentAttachmentsCard } from '#/modules/commonComponents/attachments';
import { Button } from '#/modules/commonComponents/button';
import { AppDialog } from '#/modules/commonComponents/dialog';
import FloatingInput from '#/modules/commonComponents/form/FloatingInput';
import FloatingTextarea from '#/modules/commonComponents/form/FloatingTextarea';
import { SelectCore } from '#/modules/commonComponents/form/select';
import {
  LEGACY_RECONCILIATION_STATUS_LABEL,
  localDateTimeToApi,
  nowLocalDateTime,
  subtractAmount,
  sumAmounts,
} from '#/modules/crmMgmt/customerOrder/helper/collection';
import { useCan } from '#/modules/infra/auth/useCan';
import { formatDisplayAmount } from '#/utils/formatNumber';

import CollectionSimpleTable from './CollectionSimpleTable';
import CollectionStatusBadge from './CollectionStatusBadge';
import { collectionQueryKey } from './collectionQueryKeys';
import useCollectionMutation from './useCollectionMutation';

const evidencesOf = (row) => row.evidences ?? row.evidence ?? [];
const evidenceTotal = (row) => sumAmounts(evidencesOf(row).map((evidence) => evidence.amount));
const reconciliationIdOf = (row) => row.id ?? row.reconciliationId;

/** 補登證據：可分次；`proofFileId` 選填，須為此核對項下已啟用的憑證。 */
function LegacyEvidenceDialog({ open, onOpenChange, customerOrderId, row }) {
  const [amount, setAmount] = useState('');
  const [receivedAt, setReceivedAt] = useState(nowLocalDateTime);
  const [reference, setReference] = useState('');
  const [proofFileId, setProofFileId] = useState('');
  const [formError, setFormError] = useState(null);
  const remaining = subtractAmount(row.originalDueAmount, evidenceTotal(row));

  const filesQuery = useQuery({
    queryKey: ['erp', 'customerCollectionLegacy', 'attachments', reconciliationIdOf(row)],
    queryFn: () => legacyReconciliationAttachmentsApi.fetchList(reconciliationIdOf(row)),
    enabled: open && Boolean(reconciliationIdOf(row)),
  });
  const fileOptions = [
    { value: '', label: '不附憑證' },
    ...(filesQuery.data?.data ?? []).map((file) => ({
      value: file.fileId,
      label: file.originalFilename || file.fileId,
    })),
  ];

  const mutation = useCollectionMutation(
    customerOrderId,
    (payload, { idempotencyKey }) => addLegacyReconciliationEvidence(customerOrderId, payload, { idempotencyKey }),
    { successMessage: '已補登證據', onSuccess: () => onOpenChange(false) },
  );

  function handleConfirm() {
    const actualReceivedAt = localDateTimeToApi(receivedAt);
    let error = null;
    if (!(Number(amount) > 0) || Number(amount) > remaining) {
      error = `金額須大於 0 且不超過尚未補登的 ${formatDisplayAmount(remaining)}`;
    } else if (!actualReceivedAt) {
      error = '請填寫實際收款時間';
    } else if (!reference.trim()) {
      error = '請填寫依據（例如銀行對帳單第幾筆）';
    }
    setFormError(error);
    if (error || mutation.isPending) {
      return;
    }
    mutation.mutate({
      salesOrderId: row.salesOrderId,
      amount: Number(amount),
      actualReceivedAt,
      evidenceReference: reference.trim(),
      ...(proofFileId ? { proofFileId } : {}),
      expectedVersionNo: row.versionNo,
    });
  }

  return (
    <AppDialog
      open={open}
      onOpenChange={onOpenChange}
      title="補登歷史收款證據"
      description="證據不計入新收款帳，也不改變子單原本的 PAID。憑證請先在「憑證」上傳。"
      headerVariant="muted"
      maxWidthClassName="sm:max-w-xl"
      closeOnOutsideClick={false}
      onCancel={() => onOpenChange(false)}
      onConfirm={handleConfirm}
      confirmText={mutation.isPending ? '送出中…' : '補登'}
      confirmDisabled={mutation.isPending}
      cancelText="取消"
    >
      <div className="space-y-3 pt-2.5">
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <FloatingInput
            id="legacy-evidence-amount"
            type="number"
            label="實收金額"
            min={0}
            step="0.01"
            value={amount}
            onChange={(event) => setAmount(event.target.value)}
          />
          <FloatingInput
            id="legacy-evidence-received-at"
            type="datetime-local"
            label="實際收款時間"
            value={receivedAt}
            onChange={(event) => setReceivedAt(event.target.value)}
          />
        </div>
        <FloatingInput
          id="legacy-evidence-reference"
          label="依據"
          value={reference}
          onChange={(event) => setReference(event.target.value)}
        />
        <SelectCore
          id="legacy-evidence-proof"
          label="憑證（選填）"
          options={fileOptions}
          value={proofFileId}
          onChange={setProofFileId}
        />
        {formError ? <p className="text-sm text-destructive">{formError}</p> : null}
      </div>
    </AppDialog>
  );
}

LegacyEvidenceDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  customerOrderId: PropTypes.string.isRequired,
  row: PropTypes.object.isRequired,
};

/** 核對完成：證據合計須精確等於原應收額；核對後附件不可再改。 */
function LegacyVerifyDialog({ open, onOpenChange, customerOrderId, row }) {
  const [attestation, setAttestation] = useState('');
  const [formError, setFormError] = useState(null);

  const mutation = useCollectionMutation(
    customerOrderId,
    (payload, { idempotencyKey }) => verifyLegacyReconciliation(
      customerOrderId,
      row.salesOrderId,
      payload,
      { idempotencyKey },
    ),
    { successMessage: '已完成核對', onSuccess: () => onOpenChange(false) },
  );

  function handleConfirm() {
    const error = attestation.trim() ? null : '請填寫核對聲明';
    setFormError(error);
    if (error || mutation.isPending) {
      return;
    }
    mutation.mutate({ expectedVersionNo: row.versionNo, attestation: attestation.trim() });
  }

  return (
    <AppDialog
      open={open}
      onOpenChange={onOpenChange}
      title="完成歷史收款核對"
      description={
        `原應收 ${formatDisplayAmount(row.originalDueAmount)}・已補登 ${formatDisplayAmount(evidenceTotal(row))}。`
        + '核對後不能再改附件。'
      }
      headerVariant="muted"
      closeOnOutsideClick={false}
      onCancel={() => onOpenChange(false)}
      onConfirm={handleConfirm}
      confirmText={mutation.isPending ? '送出中…' : '核對完成'}
      confirmDisabled={mutation.isPending}
      cancelText="取消"
    >
      <div className="pt-2.5">
        <FloatingTextarea
          id="legacy-verify-attestation"
          label="核對聲明"
          value={attestation}
          onChange={(event) => setAttestation(event.target.value)}
          rows={3}
        />
        {formError ? <p className="text-sm text-destructive">{formError}</p> : null}
      </div>
    </AppDialog>
  );
}

LegacyVerifyDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  customerOrderId: PropTypes.string.isRequired,
  row: PropTypes.object.isRequired,
};

/** 歷史 PAID 子單人工核對；沒有資料時整塊不顯示。 */
const CollectionLegacyCard = ({ detail, heading }) => {
  const customerOrderId = detail.id;
  const canConfirm = useCan(PERM.FMS_CUSTOMER_COLLECTION_CONFIRM);
  const canUpdate = useCan(PERM.FMS_CUSTOMER_COLLECTION_UPDATE);
  const [dialog, setDialog] = useState(null);

  const legacyQuery = useQuery({
    queryKey: collectionQueryKey(customerOrderId, 'legacy'),
    queryFn: () => fetchLegacyReconciliations(customerOrderId),
  });
  const rows = useMemo(() => legacyQuery.data?.data ?? [], [legacyQuery.data]);

  const columns = useMemo(() => {
    const soNoById = new Map((detail.salesOrders ?? []).map((so) => [so.id, so.salesOrderNo]));
    return [
      {
        id: 'salesOrder',
        header: '銷貨單',
        accessorFn: (row) => row.salesOrderNo ?? soNoById.get(row.salesOrderId) ?? row.salesOrderId,
        size: 140,
        enableSorting: false,
      },
      {
        id: 'status',
        header: '核對狀態',
        accessorKey: 'status',
        size: 100,
        enableSorting: false,
        cell: ({ row }) => (
          <CollectionStatusBadge status={row.original.status} labels={LEGACY_RECONCILIATION_STATUS_LABEL} />
        ),
        meta: { statusPresentation: 'badge' },
      },
      {
        id: 'originalDueAmount',
        header: '原應收',
        accessorFn: (row) => formatDisplayAmount(row.originalDueAmount),
        size: 110,
        enableSorting: false,
        meta: { cellClassName: 'td_right' },
      },
      {
        id: 'evidenceTotal',
        header: '已補登',
        accessorFn: (row) => `${formatDisplayAmount(evidenceTotal(row))}（${evidencesOf(row).length} 筆）`,
        size: 130,
        enableSorting: false,
        meta: { cellClassName: 'td_right' },
      },
      {
        id: 'actions',
        header: '操作',
        size: 220,
        enableSorting: false,
        cell: ({ row }) => {
          const item = row.original;
          const verified = item.status === 'VERIFIED';
          return (
            <div className="flex flex-wrap gap-1">
              <Button size="sm" variant="outline" onClick={() => setDialog({ type: 'files', row: item })}>
                憑證
              </Button>
              {!verified && canConfirm ? (
                <Button size="sm" variant="outline" onClick={() => setDialog({ type: 'evidence', row: item })}>
                  補登
                </Button>
              ) : null}
              {!verified && canConfirm && evidencesOf(item).length > 0 ? (
                <Button size="sm" onClick={() => setDialog({ type: 'verify', row: item })}>
                  核對
                </Button>
              ) : null}
            </div>
          );
        },
      },
    ];
  }, [detail.salesOrders, canConfirm]);

  if (rows.length === 0) {
    return null;
  }

  const closeDialog = (nextOpen) => {
    if (!nextOpen) {
      setDialog(null);
    }
  };

  return (
    <>
      {heading}
      <p className="px-4 text-xs text-slate-500">
        歷史 PAID 子單保留原狀態；「未核對」表示實收明細未知，不代表未付款。
      </p>
      <CollectionSimpleTable rows={rows} columns={columns} emptyText="沒有歷史核對項" rowIdPrefix="legacy" />
      {dialog?.type === 'evidence' ? (
        <LegacyEvidenceDialog open onOpenChange={closeDialog} customerOrderId={customerOrderId} row={dialog.row} />
      ) : null}
      {dialog?.type === 'verify' ? (
        <LegacyVerifyDialog open onOpenChange={closeDialog} customerOrderId={customerOrderId} row={dialog.row} />
      ) : null}
      {dialog?.type === 'files' ? (
        <AppDialog
          open
          onOpenChange={closeDialog}
          title="歷史核對憑證"
          description="僅財務可見；核對完成後不能再修改。"
          headerVariant="muted"
          maxWidthClassName="sm:max-w-2xl"
          showCancel={false}
        >
          <DocumentAttachmentsCard
            businessId={String(reconciliationIdOf(dialog.row))}
            ownerKey="customerCollectionLegacyReconciliation"
            api={legacyReconciliationAttachmentsApi}
            policy={ATTACHMENT_POLICY_STANDARD}
            readOnly={!canUpdate || dialog.row.status === 'VERIFIED'}
            variant="section"
          />
        </AppDialog>
      ) : null}
    </>
  );
};

CollectionLegacyCard.propTypes = {
  detail: PropTypes.shape({
    id: PropTypes.string.isRequired,
    salesOrders: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
  heading: PropTypes.node,
};

export default CollectionLegacyCard;
